<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { Button } from '@lib/components';
  import { browser } from '$app/environment';

  // Cookie name for analytics consent
  const ANALYTICS_CONSENT_COOKIE = 'moui_analytics_consent';
  // Session storage key for rejection
  const ANALYTICS_REJECTED_KEY = 'moui_analytics_rejected';

  export let message: string = 'We use cookies to analyze site traffic and improve your experience.';
  export let acceptLabel: string = 'Accept';
  export let rejectLabel: string = 'Decline';
  export let privacyLink: string = '/privacy';
  export let privacyLinkText: string = 'Privacy Policy';
  export let cookieDays: number = 365;

  const dispatch = createEventDispatcher<{ accept: void; reject: void }>();

  let visible = false;

  onMount(() => {
    if (browser) {
      // Check if already consented (cookie)
      const hasConsent = document.cookie.includes(`${ANALYTICS_CONSENT_COOKIE}=`);

      // Check if rejected this session
      const wasRejected = sessionStorage.getItem(ANALYTICS_REJECTED_KEY) === 'true';

      // Show banner only if no decision made
      if (!hasConsent && !wasRejected) {
        visible = true;
      }
    }
  });

  function setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
  }

  function handleAccept() {
    // Set persistent cookie
    setCookie(ANALYTICS_CONSENT_COOKIE, 'granted', cookieDays);
    visible = false;
    dispatch('accept');
  }

  function handleReject() {
    // Store rejection in sessionStorage (resets on new session)
    sessionStorage.setItem(ANALYTICS_REJECTED_KEY, 'true');
    visible = false;
    dispatch('reject');
  }
</script>

{#if visible}
<div
  class="analytics-consent-banner"
  transition:fly={{ y: 100, duration: 300 }}
>
  <div class="banner-content">
    <p class="consent-message">{message}</p>
    <a href={privacyLink} target="_blank" rel="noopener noreferrer" class="privacy-link">
      {privacyLinkText}
    </a>
    <div class="consent-actions">
      <Button
        label={acceptLabel}
        variant="secondary"
        size="sm"
        inline={true}
        on:click={handleAccept}
      />
      <Button
        label={rejectLabel}
        variant="subtle"
        size="sm"
        inline={true}
        on:click={handleReject}
      />
    </div>
  </div>
</div>
{/if}

<style lang="postcss">
  .analytics-consent-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: var(--theme-background-color, #f9f9f9);
    border-top: 1px solid var(--theme-border-color, #e5e7eb);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    font-family: var(--theme-font-family, var(--font-family, system-ui, sans-serif));
  }

  .banner-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1rem 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .consent-message {
    flex: 1;
    margin: 0;
    font-size: 0.875rem;
    min-width: 200px;
    color: var(--theme-text-color, #374151);
    line-height: 1.5;
  }

  .privacy-link {
    color: var(--theme-text-color, #3b82f6);
    font-size: 0.875rem;
    text-decoration: underline;
    white-space: nowrap;
  }

  .privacy-link:hover {
    opacity: 0.8;
  }

  .consent-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    .banner-content {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
    }

    .consent-actions {
      justify-content: flex-end;
    }

    .privacy-link {
      align-self: flex-start;
    }
  }
</style>
